import { Link } from "react-router-dom";
import { testimonials } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useState } from "react";

export default function About() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) { setFormStatus("success"); setFormMessage("提交成功！我们会尽快与您联系。"); return; }
    try {
      const formDataObj = new FormData(form);
      const response = await fetch("http://uhomekj.com/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormMessage("提交成功！感谢您的留言，我们会尽快回复您。");
        setFormData({ name: "", email: "", message: "" });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus("error");
        setFormMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络错误，请检查网络后重试。");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "http://uhomekj.com/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "http://uhomekj.com/about" }
        ]
      },
      {
        "@type": "AboutPage",
        name: "关于我们 - 韩漫大全漫画网",
        description: "韩漫大全漫画网是国内领先的韩国漫画在线阅读平台，提供下拉式免费阅读、无删减韩漫内容。",
        url: "http://uhomekj.com/about",
        isPartOf: { "@type": "WebSite", name: "韩漫大全漫画网", url: "http://uhomekj.com/" }
      },
      {
        "@type": "Organization",
        name: "韩漫大全漫画网",
        url: "http://uhomekj.com/",
        description: "国内领先的韩国漫画在线阅读平台",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "400-888-9999",
          email: "hello@uhomekj.com",
          contactType: "customer service"
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "上海",
          addressRegion: "上海",
          addressCountry: "CN",
          streetAddress: "浦东新区张江高科技园区"
        }
      }
    ]
  };

  usePageMeta({
    title: "关于我们_韩漫大全漫画网_品牌介绍与联系方式",
    description: "韩漫大全漫画网是国内领先的韩国漫画在线阅读平台，提供下拉式免费阅读、无删减韩漫内容。了解品牌故事、用户口碑与联系方式。",
    keywords: "韩漫大全漫画网,关于我们,韩漫平台,品牌介绍,联系方式",
    canonical: "http://uhomekj.com/about",
    jsonLd,
  });

  const features = [
    { icon: "ri-shield-check-line", label: "正版授权" },
    { icon: "ri-flashlight-line", label: "极速更新" },
    { icon: "ri-smartphone-line", label: "多端适配" },
    { icon: "ri-translate", label: "专业翻译" },
    { icon: "ri-heart-line", label: "用户至上" }
  ];

  return (
    <>
      <Navbar />

      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20library%20reading%20room%20background%2C%20soft%20golden%20light%20streaming%20through%20windows%2C%20wooden%20bookshelves%20with%20books%2C%20cozy%20warm%20cream%20and%20beige%20tones%2C%20minimalist%20elegant%20interior%2C%20dreamy%20soft%20focus%20cinematic%20composition%2C%20no%20text%20no%20people&width=1920&height=800&seq=about-hero-04&orientation=landscape"
            alt="关于韩漫大全漫画网"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>
        <div className="relative z-10 h-full w-full px-4 md:px-6 flex flex-col justify-center">
          <div className="max-w-3xl">
            <h1 className="font-heading text-2xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
              关于我们
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-4 max-w-lg leading-relaxed">
              致力于为华语用户提供最优质的韩漫阅读体验
            </p>
          </div>
        </div>
      </section>

      <main className="bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="py-8">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-foreground-950">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-accent-400 text-xs font-medium uppercase tracking-wider mb-4">品牌故事</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-6 leading-snug">
              让每一位韩漫爱好者<br />都能享受极致的阅读体验
            </h2>
            <p className="text-foreground-400 text-sm md:text-base leading-relaxed mb-10 max-w-2xl mx-auto">
              韩漫大全漫画网成立于2020年，是国内最早专注于韩国漫画在线阅读的平台之一。多年来，我们始终坚持"免费、完整、快速"的服务理念，为超过580万用户提供了优质的韩漫阅读服务。从最初的几十部漫画到如今超过12,800部的庞大内容库，我们与用户共同成长，不断优化阅读体验，推动韩漫文化在华语地区的传播。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/category/romance"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-all cursor-pointer"
              >
                立即开始阅读
              </Link>
              <a
                href="#contact"
                className="px-8 py-3 bg-transparent border border-white/30 text-white rounded-full text-sm font-medium whitespace-nowrap hover:bg-white/10 transition-all cursor-pointer"
              >
                联系我们
              </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mt-14">
              {features.map((feat) => (
                <div key={feat.label} className="bg-foreground-900/50 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${feat.icon} text-lg text-primary-400`}></i>
                  </div>
                  <span className="text-xs text-background-100 whitespace-nowrap">{feat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                来自真实用户的评价，是我们前进的最大动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-6 flex flex-col">
                  <div className="text-accent-500 text-3xl font-serif mb-3">&ldquo;</div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-6">
                    {t.content}
                  </p>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.author}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                有任何问题或建议，欢迎随时与我们联系
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <form
                 
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white border border-background-300/60 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white border border-background-300/60 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-white border border-background-300/60 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-anti-spam"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
                  >
                    提交留言
                  </button>
                  {formStatus !== "idle" && (
                    <p className={`text-sm text-center ${formStatus === "success" ? "text-green-600" : "text-red-500"}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>

              <div className="lg:w-80 flex-shrink-0 space-y-6">
                <div className="bg-background-50 rounded-lg p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950">电子邮件</h3>
                      <p className="text-xs text-foreground-500">hello@uhomekj.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950">客服电话</h3>
                      <p className="text-xs text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950">公司地址</h3>
                      <p className="text-xs text-foreground-500">上海市浦东新区张江高科技园区</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg p-5">
                  <h3 className="text-sm font-bold text-foreground-950 mb-3">工作时间</h3>
                  <div className="space-y-2 text-xs text-foreground-500">
                    <p>周一至周五：9:00 - 18:00</p>
                    <p>周六：10:00 - 17:00</p>
                    <p>周日：休息</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}